// Pure helpers for the admin charter pages (spec 6.4). Money is integer
// cents on the wire and is formatted by hand as "R 14,450" (spec 2.2, D23),
// never with Intl currency formatting. Times show in Africa/Johannesburg,
// whatever the browser's zone.

export const SAST = 'Africa/Johannesburg';
export const LIVE_STATUSES = ['requested', 'awaiting_payment', 'confirmed'];
export const LICENCE_CODES = ['B', 'C1', 'C', 'EB', 'EC1', 'EC'];

export const DOC_TYPE_LABELS = {
  operating_licence: 'Operating licence',
  prdp_p: 'PrDP-P',
  passenger_liability_insurance: 'Passenger liability insurance',
};

export const CHARTER_STATUS_LABELS = {
  none: 'Not applied',
  pending: 'Applied, waiting for review',
  approved: 'Approved for charter',
  rejected: 'Rejected',
};

export const HIRE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'requested', label: 'Requested' },
  { id: 'confirmed', label: 'Confirmed' },
  { id: 'needs_action', label: 'Needs action' },
  { id: 'completed', label: 'Completed' },
];

export const PAYMENT_STATE_LABELS = {
  none: 'Nothing paid',
  deposit_paid: 'Deposit paid',
  balance_due: 'Balance due',
  paid: 'Paid in full',
  partially_refunded: 'Partly refunded',
  refunded: 'Refunded',
};

export const NEEDS_ACTION_LABELS = {
  awaiting_payment: 'Awaiting payment',
  balance_due: 'Balance due',
  no_driver: 'No driver, under 72h',
  operator_unapproved: 'Operator no longer approved',
  s17_5_claimed: 's17(5) claimed',
};

export const REFUND_REASONS = [
  { value: 's17_5', label: 'Death or hospitalisation (s17(5))' },
  { value: 'operator_no_show', label: 'Operator did not show' },
  { value: 'goodwill', label: 'Goodwill' },
  { value: 'other', label: 'Other' },
];

export const CANCEL_REASONS = [
  { value: 'operator_no_show', label: 'Operator cannot do the trip' },
  { value: 'traveller_request', label: 'The traveller asked us to cancel' },
  { value: 'safety', label: 'Safety concern' },
  { value: 'other', label: 'Other' },
];

const EXPIRED_LABELS = {
  no_response: 'Expired: no reply',
  payment_not_completed: 'Expired: not paid',
  operator_unavailable: 'Expired: operator unavailable',
};

const WHO = { traveler: 'the traveller', operator: 'the operator', admin: 'Zuttle', system: 'the system' };

/** "R 14,450", "R 12,282.50", "-R 10". */
export function formatRand(cents) {
  const n = Number(cents) || 0;
  const abs = Math.abs(Math.round(n));
  const rand = Math.floor(abs / 100);
  const rest = abs % 100;
  const grouped = String(rand).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${n < 0 ? '-' : ''}R ${grouped}${rest ? `.${String(rest).padStart(2, '0')}` : ''}`;
}

/** Rand typed by a person ("1 445", "1,445.50") to whole cents, or null. */
export function randToCents(text) {
  const cleaned = String(text ?? '').replace(/[\sR,]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const [whole, frac = ''] = cleaned.split('.');
  return Number(whole) * 100 + Number(frac.padEnd(2, '0'));
}

const parts = (date, options) => {
  const out = {};
  new Intl.DateTimeFormat('en-ZA', { timeZone: SAST, ...options })
    .formatToParts(new Date(date))
    .forEach((p) => { out[p.type] = p.value; });
  return out;
};

/** "Fri 4 Dec 2026" */
export function formatDay(date) {
  if (!date) return '';
  const p = parts(date, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  return `${p.weekday} ${Number(p.day)} ${p.month} ${p.year}`;
}

/** "07:00" */
export function formatTime(date) {
  if (!date) return '';
  const p = parts(date, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  return `${p.hour}:${p.minute}`;
}

export const formatDateTime = (date) => (date ? `${formatDay(date)} ${formatTime(date)}` : '');

/** "Fri 4 Dec 2026 07:00 to Sun 6 Dec 2026 18:00", or one day with two times. */
export function formatRange(startAt, endAt) {
  if (!startAt || !endAt) return '';
  return formatDay(startAt) === formatDay(endAt)
    ? `${formatDay(startAt)} ${formatTime(startAt)} to ${formatTime(endAt)}`
    : `${formatDateTime(startAt)} to ${formatDateTime(endAt)}`;
}

/** A date as yyyy-mm-dd in SAST, for a date input. */
export function sastDateInput(date) {
  if (!date) return '';
  const p = parts(date, { year: 'numeric', month: '2-digit', day: '2-digit' });
  return `${p.year}-${p.month}-${p.day}`;
}

/** The admin's label for a hire's status (spec 4.6). */
export function hireStatusLabel(hire) {
  if (!hire) return '';
  switch (hire.status) {
    case 'requested': return 'Requested';
    case 'awaiting_payment': return 'Awaiting payment';
    case 'confirmed': return 'Confirmed';
    case 'completed': return 'Completed';
    case 'declined': return 'Declined by the operator';
    case 'expired': return EXPIRED_LABELS[hire.expiredReason] || 'Expired';
    case 'cancelled': {
      const by = hire.cancellation && WHO[hire.cancellation.by];
      return by ? `Cancelled by ${by}` : 'Cancelled';
    }
    default: return hire.status || '';
  }
}

export const isLive = (hire) => Boolean(hire) && LIVE_STATUSES.includes(hire.status);

/** Who or what a document belongs to. */
export function ownerLabel(doc) {
  const o = doc && doc.ownerInfo;
  if (!o) return 'Unknown';
  if (doc.ownerType === 'vehicle') return [o.vehicleMake, o.model, o.licensePlate && `(${o.licensePlate})`].filter(Boolean).join(' ');
  if (doc.ownerType === 'driver') return o.name || 'Driver';
  return o.companyName || 'Company';
}

const TIMELINE_LABELS = {
  requested: 'Requested by the traveller',
  accepted: 'Accepted by the operator',
  rejected: 'Rejected by the operator',
  driver_assigned: 'Driver assigned',
  cancelled: 'Cancelled',
  expired: 'Expired',
  completed: 'Completed',
  notice_driverDue72: 'Operator told to assign a driver (T-72h)',
  notice_driverDue24: 'Founders told: no driver (T-24h)',
  notice_operatorUnapproved: 'Founders told: operator no longer approved',
  notice_unresolvedPayment: 'Founders told: payment unresolved',
};

/** One timeline event as a line of text. */
export function timelineText(event) {
  if (!event) return '';
  if (TIMELINE_LABELS[event.event]) {
    let text = TIMELINE_LABELS[event.event];
    if (event.event === 'cancelled' && event.by) text += ` by ${WHO[event.by] || event.by}`;
    if (event.event === 'expired' && event.reason) text = EXPIRED_LABELS[event.reason] || text;
    if (event.reason && event.event === 'cancelled') text += ` (${event.reason})`;
    if (event.note) text += `: ${event.note}`;
    return text;
  }
  const m = /^(deposit|full|balance|refund)_(.+)$/.exec(event.event || '');
  if (m) {
    const kind = { deposit: 'Deposit', full: 'Full price', balance: 'Balance', refund: 'Refund' }[m[1]];
    const what = m[2] === 'started' ? 'started' : m[2].replace(/_/g, ' ');
    const amount = event.amountCents !== undefined ? ` ${formatRand(event.amountCents)}` : '';
    const failure = event.failureReason ? ` (${event.failureReason})` : '';
    return `${kind}${amount} ${what}${failure}`;
  }
  return String(event.event || '').replace(/_/g, ' ');
}

/** What an error from the API should say to the admin. */
export function apiErrorMessage(error, fallback = 'Something went wrong') {
  const data = error && error.response && error.response.data;
  if (data && data.code === 'HIRE_CHANGED') return 'This hire changed since you opened it. It has been reloaded: check it and try again.';
  if (data && data.code === 'REFUND_TOO_LARGE') return `That is more than can be refunded (${formatRand(data.refundableCents)}).`;
  return (data && data.message) || (error && error.message) || fallback;
}
